import { getAgenticChatModeLedgerLargeText } from "./ledgerAgenticChatModeLabels.js";
import { resolveModelLabel } from "./engineLabelResolver.js";

// A Muse 2 Alpha comparison runs on an ordinary engine underneath; the billing
// context labels those entries so history shows the comparison, not the engine.
const writeModelText = (entry, func) => entry.context?.modelLabel || writeLabelMap[func];

const numberWithCommas = (x) => {
  if (x === null || x === undefined) return "";
  if (x === 0) return x;
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const writeLabelMap = {
  fastest: "Fastest",
  mostAccurate: "Most Accurate",
  basic: "Basic",
  excellent: "Excellent",
  "muse-2-alpha": "Muse 2 Alpha",
  balanced: "Balanced",
  unfiltered: "Unfiltered",
  bestProse: "Best Prose (v1)",
  gpt4turbo: "GPT 4-Turbo",
  gpt4o: "GPT 4o",
  gpt4omini: "GPT 4o Mini",
  mythomax: "Mythomax 13B",
  mistral7bOpenOrca: "Open Orca Mistral 7B",
  mistralMedium: "Mistral Medium",
  mistralLarge: "Mistral Large 3",
  starCannon: "Star Cannon",
  deepSeekR1: "DeepSeek R1",
  deepSeekV3: "DeepSeek V3",
  deepSeekV31: "DeepSeek V3.1",
  deepSeekV4Pro: "DeepSeek V4 Pro",
  deepSeekInstant: "DeepSeek Instant",
  openHermes2Mistral7b: "OpenHermes Mistral 7B",
  airoboros70b: "Airoboros 70B",
  goliath120b: "Goliath 120B",
  gemini: "Gemini Pro 2.5",
  geminiFlashThinking: "Gemini 2.5 Flash Thinking",
  gemini3Pro: "Gemini 3 Pro",
  gemini3Flash: "Gemini 3 Flash",
  gemini37Flash: "Gemini 3.7 Flash",
  gemini38Flash: "Gemini 3.8 Flash",
  glm53: "GLM 5.3",
  glm53Flash: "GLM 5.3 Flash",
  qwen38Max: "Qwen 3.8 Max",
  gemini31Pro: "Gemini 3.1 Pro",
  kimiK2: "Kimi K2",
  glm52: "GLM 5.2",
  kimiK3: "Kimi K3",
  kimiK2Thinking: "Kimi K2 Thinking",
  claude3opus: "Premium",
  claude3opusRaw: "Claude 3 Opus", // this is the exact same as above, but we need to distinguish them in the UI
  claude3sonnet: "Claude 3 Sonnet",
  claude3haiku: "Claude 3 Haiku",
  claude35sonnet: "Claude 3.5 Sonnet",
  claude35haiku: "Claude 3.5 Haiku",
  claude37sonnet: "Claude 3.7 Sonnet",
  claude4sonnet: "Claude 4 Sonnet",
  claude45sonnet: "Claude 4.5 Sonnet",
  claude46sonnet: "Claude 4.6 Sonnet",
  claude5sonnet: "Claude 5 Sonnet",
  claude55sonnet: "Claude 5.5 Sonnet",
  claude45haiku: "Claude 4.5 Haiku",
  claude55haiku: "Claude 5.5 Haiku",
  claude4opus: "Claude 4 Opus",
  claude41opus: "Claude 4.1 Opus",
  claude45opus: "Claude 4.5 Opus",
  claude46opus: "Claude 4.6 Opus",
  claude47opus: "Claude 4.7 Opus",
  claude48opus: "Claude 4.8 Opus",
  claude5opus: "Claude 5 Opus",
  claude55opus: "Claude 5.5 Opus",
  claude5fable: "Claude 5 Fable",
  claude51fable: "Claude 5.1 Fable",
  muse: "Sudowrite Muse",
  muse2merge: "Muse 2 Merge",
  "ballad-write": "Ballad Write",
  "ballad-rewrite": "Ballad Rewrite",
  "ballad-rewrite-v11": "Ballad Rewrite V11",
  "ballad-draft": "Ballad Draft",
  "ballad-1.1": "Ballad 1.1", // legacy A/B key, renamed to ballad-1.1-write
  "ballad-1.1-write": "Ballad 1.1 Write",
  "ballad-1.1-draft": "Ballad 1.1 Draft",
  // Legacy engine keys — renamed to ballad-write / ballad-draft. Kept here so
  // historical ledger entries with the old keys still render a friendly label.
  pencil: "Ballad Write",
  chalk: "Ballad Draft",
  mixtral8x7bInstruct: "Mixtral 7B Instruct",
  gpt41: "GPT 4.1",
  gpt41mini: "GPT 4.1 Mini",
  gpt41nano: "GPT 4.1 Nano",
  gpt5: "GPT 5",
  gpt5mini: "GPT 5 Mini",
  gpt5nano: "GPT 5 Nano",
  gpt54: "GPT 5.4",
  gpt55: "GPT 5.5",
  gpt56sol: "GPT 5.6 Sol",
  gpt6astra: "GPT-6 Astra",
  gpt61sol: "GPT-6.1 Sol",
  gpt6sol: "GPT-6 Sol",
  gpt6luna: "GPT-6 Luna",
  gpt56terra: "GPT 5.6 Terra",
  gpt56luna: "GPT 5.6 Luna",
  o3: "o3",
  grok4: "Grok 4",
  grok43: "Grok 4.3",
  grok47: "Grok 4.7",
  myVoice: "My Voice",
  muse1: "Muse 1",
  muse2Leonardo: "Leonardo",
  muse2Michelangelo: "Michelangelo",
  muse2Donatello: "Donatello",
  muse2Raphael: "Raphael",
};

const writeLabelMapCaseInsensitive = Object.fromEntries(
  Object.entries(writeLabelMap).map(([key, value]) => [key.toLowerCase(), value]),
);

/**
 * Provider engine paths for Write button models (WRITE_MODEL_TO_ENGINE) that are not
 * writeModelId keys. Labels match WRITE_MODELS in constants.js.
 */
const writeModelProviderPathLabels = {
  "claude-3-5-haiku-20241022": "Claude 3.5 Haiku",
  "claude-3-haiku-20240307": "Claude 3 Haiku",
  "claude-haiku-4-5-20251001": "Claude 4.5 Haiku",
  "claude-haiku-5-5": "Claude 5.5 Haiku",
  "claude-opus-4-1-20250805": "Claude 4.1 Opus",
  "claude-opus-4-20250514": "Claude 4 Opus",
  "claude-opus-4-5-20251101": "Claude 4.5 Opus",
  "claude-opus-4-6": "Claude 4.6 Opus",
  "claude-opus-4-7": "Claude 4.7 Opus",
  "claude-opus-4-8": "Claude 4.8 Opus",
  "claude-opus-5": "Claude 5 Opus",
  "claude-opus-5-5": "Claude 5.5 Opus",
  "claude-fable-5": "Claude 5 Fable",
  "claude-fable-5-1": "Claude 5.1 Fable",
  "claude-sonnet-4-20250514": "Claude 4 Sonnet",
  "claude-sonnet-4-5-20250929": "Claude 4.5 Sonnet",
  "claude-sonnet-4-6": "Claude 4.6 Sonnet",
  "claude-sonnet-5": "Claude 5 Sonnet",
  "claude-sonnet-5-5": "Claude 5.5 Sonnet",
  "deepseek/deepseek-chat": "DeepSeek V3",
  "deepseek/deepseek-chat-v3.1": "DeepSeek V3.1",
  "deepseek/deepseek-v3.1": "DeepSeek V3.1",
  "deepseek/deepseek-r1": "DeepSeek R1",
  "deepseek/deepseek-v4-pro": "DeepSeek V4 Pro",
  "gemini-2.5-flash": "Gemini 2.5 Flash Thinking",
  "gemini-2.5-pro": "Gemini Pro 2.5",
  "gemini-3-flash-preview": "Gemini 3 Flash",
  "gemini-3.8-flash": "Gemini 3.8 Flash",
  "gemini-3.1-pro-preview": "Gemini 3.1 Pro",
  "gpt-3.5-turbo": "Fastest",
  "gpt-4-turbo": "Balanced",
  "gpt-4.1": "GPT 4.1",
  "gpt-4.1-mini": "GPT 4.1 Mini",
  "gpt-4.1-nano": "GPT 4.1 Nano",
  "gpt-4o": "Best Prose (v1)",
  "gpt-4o-2024-11-20": "GPT 4o",
  "gpt-4o-mini": "GPT 4o Mini",
  "gpt-5": "GPT 5",
  "gpt-5-mini": "GPT 5 Mini",
  "gpt-5-nano": "GPT 5 Nano",
  "gpt-5.4": "GPT 5.4",
  "gpt-5.5": "GPT 5.5",
  "gpt-5.6-sol": "GPT 5.6 Sol",
  "gpt-6-astra": "GPT-6 Astra",
  "gpt-6.1-sol": "GPT-6.1 Sol",
  "gpt-6-sol": "GPT-6 Sol",
  "gpt-6-luna": "GPT-6 Luna",
  "gpt-5.6-terra": "GPT 5.6 Terra",
  "gpt-5.6-luna": "GPT 5.6 Luna",
  "gryphe/mythomax-l2-13b": "Mythomax 13B",
  "jondurbin/airoboros-l2-70b": "Airoboros 70B",
  "kimi-k2": "Kimi K2",
  "kimi-k2-thinking": "Kimi K2 Thinking",
  "kimi-k2.7-code": "Kimi K2.7 Code",
  "moonshotai/kimi-k2.7-code": "Kimi K2.7 Code",
  "kimi-k2.6": "Kimi K2.6",
  "kimi-k3": "Kimi K3",
  "mancer/weaver": "Unfiltered",
  "mistralai/mistral-large-2512": "Mistral Large 3",
  "mistralai/mistral-medium": "Mistral Medium",
  "moonshotai/kimi-k2": "Kimi K2",
  "moonshotai/kimi-k2-thinking": "Kimi K2 Thinking",
  "moonshotai/kimi-k2.6": "Kimi K2.6",
  "moonshotai/kimi-k3": "Kimi K3",
  muse: "Muse",
  muse1: "Muse 1",
  myVoice: "My Voice",
  o3: "o3",
  "x-ai/grok-4.3": "Grok 4.3",
  "x-ai/grok-4.6": "Grok 4.6",
};

const writeModelProviderPathLabelsCaseInsensitive = Object.fromEntries(
  Object.entries(writeModelProviderPathLabels).map(([key, value]) => [key.toLowerCase(), value]),
);

/**
 * User-facing write model label (matches chiclet / model picker), from a writeModelId,
 * `write-button/<id>`, or provider path.
 */
export function resolveWriteModelUserDisplayName(modelId) {
  const raw = String(modelId ?? "").trim();
  if (!raw) return "";

  const withoutWriteButtonPrefix = raw.replace(/^write-button\//i, "").trim();

  const fromWriteModels = resolveModelLabel(withoutWriteButtonPrefix);
  if (fromWriteModels) return fromWriteModels;

  const fromWriteLabel =
    writeLabelMap[withoutWriteButtonPrefix]
    ?? writeLabelMapCaseInsensitive[withoutWriteButtonPrefix.toLowerCase()];
  if (fromWriteLabel) return fromWriteLabel;

  const fromProvider =
    writeModelProviderPathLabels[withoutWriteButtonPrefix]
    ?? writeModelProviderPathLabelsCaseInsensitive[withoutWriteButtonPrefix.toLowerCase()];
  if (fromProvider) return fromProvider;

  if (withoutWriteButtonPrefix.includes("/")) {
    const lastSegment = withoutWriteButtonPrefix.split("/").pop() ?? "";
    const fromLast =
      writeLabelMap[lastSegment]
      ?? writeLabelMapCaseInsensitive[lastSegment.toLowerCase()];
    if (fromLast) return fromLast;
  }

  return withoutWriteButtonPrefix;
}

const expandLabelMap = {
  muse: "Sudowrite Muse",
};

const getEntrySmallText = (entry, engineTexts, errorCallback) => {
  // if there are no completions, the engine will be undefined
  // typically this occurs when there is an error in the stream
  // so we tell the user the invocation did not complete
  // and no credits were charged
  if (!entry.context?.completions || entry.context?.completions.length === 0) {
    return "Error. No credits used.";
  }

  const engineText = entry.context?.completions.map((completion) => engineTexts[completion.engine]).find((engineText) => engineText !== undefined);

  if (!engineText && errorCallback) {
    errorCallback(new Error("No engine text found for entry"), { entry, engineTexts });
  }

  return engineText?.smallText || "Error or Unknown engine";
};

export const getStoryBibleLedgerContextLabel = (entry) => {
  const label = entry?.context?.storyBibleLedgerLabel;
  if (typeof label !== "string") return null;
  const trimmed = label.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const matches = [
  {
    matchType: "ledgerEntryType",
    matchValue: "rollover", // i.e. pack credits that never expire
    transform: (entry) => {
      return { largeText: "Non-Expiring Rollover", amount: entry.amount };
    },
  },
  {
    matchType: "ledgerEntryType",
    matchValue: "expiring_rollover", // unused credits from prev months that expires eventually
    transform: (entry) => {
      return { largeText: "Expiring Rollover", amount: entry.amount };
    },
  },
  {
    matchType: "ledgerEntryType",
    matchValue: "cancelled", // entry for the cancelled subscription
    transform: (entry) => {
      return { largeText: "Cancelled credits", amount: entry.amount };
    },
  },
  {
    matchType: "ledgerEntryType",
    matchValue: "benchmark_refund", // generation credits returned for a completed Muse 2 Alpha comparison
    transform: (entry) => {
      return { largeText: "Muse 2 Alpha Refund", smallText: "Credits returned for a completed comparison", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "initial trial ledger",
    transform: (entry) => {
      return { largeText: "Trial Allotment", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "anonymous-subscription",
    transform: (entry) => {
      return { largeText: "Bonus Credits", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "story-bible-tour-experiment",
    transform: (entry) => {
      return { largeText: "Story Bible Tour Allotment", smallText: "Credits for the Story Bible tour.", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "story-bible-tour-complete",
    transform: (entry) => ({
      largeText: "Tour Complete",
      smallText: "Reset to Standard Trial Credits",
      amount: entry.amount,
    }),
  },
  {
    matchType: "source",
    matchValue: "restored trial ledger",
    transform: (entry) => {
      return { largeText: "Restored Trial Allotment", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "trialAdjustment",
    transform: (entry) => {
      if (entry.context?.adjustmentSource) {
        if (entry.context?.adjustmentSource === "storyEngine/characterCast") {
          return { largeText: "Trial Adjustment", smallText: "For Generate All Characters", amount: entry.amount };
        }
      }
      return { largeText: "Trial Adjustment", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "monthly allotment",
    transform: (entry) => {
      return { largeText: "Monthly Allotment", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "grace",
    transform: (entry) => {
      return { largeText: "Grace", amount: entry.amount };
    },
  },
  {
    // note: this does not distinguish between expiring and non-expiring admin interface adjustments
    // the distinction could be confusing for the user, but we can add it in if we want
    matchType: "source",
    matchValue: "admin-interface",
    transform: (entry) => {
      return { largeText: "Admin Adjustment", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "script",
    transform: (entry) => {
      const note = typeof entry.note === "string" && entry.note.trim() ? entry.note.trim() : null;
      return { largeText: note || "Script Adjustment", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "pack-purchase-zero",
    transform: (entry) => {
      return { largeText: "Balance Reset", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "pack-purchase",
    transform: (entry) => {
      return { largeText: "Pack Purchase", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "command",
    transform: (entry) => {
      return { largeText: "First Draft", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "multiverse",
    transform: (entry) => {
      return { largeText: "Write", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "guidance",
    transform: (entry) => {
      return { largeText: "Guided Write", smallText: "Guidance", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "guidedWormhole",
    transform: (entry) => {
      return { largeText: "Guided Write", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "rewrite",
    transform: (entry) => {
      return { largeText: "Rewrite", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "myVoiceRewrite",
    transform: (entry) => {
      return { largeText: "My Voice", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "process_user_style_sample",
    transform: (entry) => {
      return { largeText: "My Voice Training", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "brainstorm",
    transform: (entry) => {
      return { largeText: "Brainstorm", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "twist",
    transform: (entry) => {
      return { largeText: "Twist", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "poem",
    transform: (entry) => {
      return { largeText: "Poem", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "description",
    transform: (entry) => {
      return { largeText: "Describe", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "expand",
    transform: (entry) => {
      return { largeText: "Expand", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "documentTitle",
    transform: () => {
      return false;
    },
  },
  {
    matchType: "source",
    matchValue: "persistentChat/generateTitle",
    transform: () => {
      return false;
    },
  },
  {
    matchType: "source",
    matchValue: "agenticChat/generateTitle",
    transform: () => {
      return false;
    },
  },
  {
    matchType: "sourcePattern",
    matchValue: "^runDynamicPluginAction/documentPlugins/expand/.*$",
    transform: (entry) => {
      const func = entry.source.split("/").pop();

      return { largeText: "Expand", smallText: expandLabelMap[func], amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "shrinkRay",
    transform: (entry) => {
      return { largeText: "Shrink Ray", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "character",
    transform: (entry) => {
      return { largeText: "Characters", amount: entry.amount }; // note: this is not SB characters box
    },
  },
  {
    matchType: "source",
    matchValue: "visualize",
    transform: (entry) => {
      return { largeText: "Visualize", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "characterPortrait",
    transform: (entry) => {
      return { largeText: "Character Portrait Gallery", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "worldbuildingImage",
    transform: (entry) => {
      return { largeText: "Worldbuilding Image Gallery", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/style/analyze",
    transform: (entry) => {
      return { largeText: "Match My Style", smallText: "Analyze", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/style/analyze-detailed",
    transform: () => false,
  },
  {
    matchType: "source",
    matchValue: "storyEngine/style/compress",
    transform: (entry) => {
      return { largeText: "Match My Style", smallText: "Compress", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/style/generate-sample",
    transform: () => false,
  },
  {
    matchType: "source",
    matchValue: "storyEngine/synopsis",
    transform: (entry) => {
      return { largeText: "Generate Synopsis", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/classify-genre",
    transform: (entry) => {
      return false;
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/classifyGenre",
    transform: (entry) => {
      return { largeText: "Classify Genre", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/rewrite/synopsis",
    transform: (entry) => {
      return { largeText: "Rewrite Synopsis", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/characters",
    transform: (entry) => {
      return { largeText: "Generate Characters", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/character",
    transform: (entry) => {
      return { largeText: "Generate Character", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/upgradeCast",
    transform: (entry) => {
      return { largeText: "Upgrade Cast", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/characterCast",
    transform: (entry) => {
      return { largeText: "Generate All Characters", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/regenerateTrait",
    transform: (entry) => {
      return { largeText: "Regenerate Trait", smallText: "Character", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/regenerateWorldbuildingTrait",
    transform: (entry) => {
      return { largeText: "Regenerate Trait", smallText: "Worldbuilding", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/worldbuildingEntry",
    transform: (entry) => {
      return { largeText: "Generate Element", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/rewrite/characters",
    transform: (entry) => {
      return { largeText: "Rewrite Characters", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/outline/novel-outline",
    transform: (entry) => {
      return { largeText: "Generate Outline", smallText: "Novel Outline", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/outline/novel-outline-structured",
    transform: (entry) => {
      return { largeText: "Generate Outline", smallText: "Novel Outline", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/outline/heros-journey",
    transform: (entry) => {
      return { largeText: "Generate Outline", smallText: "The Hero's Journey", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/outline/heros-journey-structured",
    transform: (entry) => {
      return { largeText: "Generate Outline", smallText: "The Hero's Journey", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/outline/hollywood-beats",
    transform: (entry) => {
      return { largeText: "Generate Outline", smallText: "Hollywood Beats", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/outline/hollywood-beats-structured",
    transform: (entry) => {
      return { largeText: "Generate Outline", smallText: "Hollywood Beats", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/outline/story-circle",
    transform: (entry) => {
      return { largeText: "Generate Outline", smallText: "Story Circle", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/outline/story-circle-structured",
    transform: (entry) => {
      return { largeText: "Generate Outline", smallText: "Story Circle", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/outline/romance-outline",
    transform: (entry) => {
      return { largeText: "Generate Outline", smallText: "Romance Outline", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/outline/romance-outline-structured",
    transform: (entry) => {
      return { largeText: "Generate Outline", smallText: "Romance Outline", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/rewrite/outline",
    transform: (entry) => {
      return { largeText: "Rewrite Outline", smallText: "", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/outline/rewrite-outline-chapter",
    transform: (entry) => {
      return { largeText: "Rewrite Outline Chapter", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/rewrite/structured-outline",
    transform: (entry) => {
      return { largeText: "Rewrite Outline", smallText: "", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/outline/generate-outline-chapter",
    transform: (entry) => {
      return { largeText: "Generate Outline Chapter", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/generate-outline-chapter",
    transform: (entry) => {
      return { largeText: "Generate Outline Chapter", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/rewrite-outline-chapter",
    transform: (entry) => {
      return { largeText: "Rewrite Outline Chapter", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/outline/generate",
    transform: (entry) => {
      return { largeText: "Generate Outline", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/outline/update-chapter-from-document",
    transform: (entry) => {
      return { largeText: "Update Outline Chapter", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/outline/custom-outline",
    transform: (entry) => {
      return { largeText: "Custom Outline", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/outline/import-outline",
    transform: (entry) => {
      return { largeText: "Import Outline", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/commands",
    transform: (entry) => {
      return { largeText: "Generate Beats", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/most-accurate",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Most Accurate", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/basic",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Basic", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/excellent",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Excellent", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/chapterGenerator",
    transform: (entry) => {
      const smallText = entry.context?.modelLabel ? entry.context.modelLabel : "Scenes";
      return { largeText: "Draft", smallText, amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/analyze-scenes",
    transform: (entry) => {
      return { largeText: "Analyze Scenes", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/generate-scenes",
    transform: (entry) => {
      return { largeText: "Generate Scenes", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/rewrite-scenes",
    transform: (entry) => {
      return { largeText: "Rewrite Scenes", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/scene-directions",
    transform: (entry) => {
      return { largeText: "Scene Directions", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/muse",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Sudowrite Muse 0.1", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/muse-02",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Sudowrite Muse 0.2", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/muse-03",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Sudowrite Muse 0.3", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/muse-04",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Sudowrite Muse 0.4", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/muse-05",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Sudowrite Muse 0.5", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/muse-06",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Sudowrite Muse 0.6", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/balanced",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Balanced", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/best-prose",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Best Prose (v1)", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/fastest",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Fastest", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/custom",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Custom", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/spicy",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Unfiltered", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/claude-3-opus",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Premium", amount: entry.amount };
    },
  },
  // note: this is the exact same as above, but we want to distinguish between the two
  // goal is to give folks looking for "Opus" label an "Opus" button
  {
    matchType: "source",
    matchValue: "storyEngine/write/claude-3-opus/raw",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Claude 3 Opus", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/claude-3-sonnet",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Claude 3 Sonnet", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/claude-3-5-sonnet",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Claude 3.5 Sonnet", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/claude-3-7-sonnet",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Claude 3.7 Sonnet", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/claude-3-haiku",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Claude 3 Haiku", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/claude-3-5-haiku",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Claude 3.5 Haiku", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/gpt-4o",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "GPT 4o", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/gpt-4o-mini",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "GPT 4o Mini", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/deepseek",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "DeepSeek R1", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/groq",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "DeepSeek Instant", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "ramble/create",
    transform: (entry) => {
      return { largeText: "Ramble Create", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "ramble/analyse",
    transform: (entry) => {
      return { largeText: "Ramble Analyse", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "ramble/merged",
    transform: (entry) => {
      return { largeText: "Ramble", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/openrouter",
    transform: (entry, errorCallback) => {
      const engineTexts = {
        "muse2-leonardo": { smallText: "Leonardo" },
        "muse2-michelangelo": { smallText: "Michelangelo" },
        "muse2-donatello": { smallText: "Donatello" },
        "muse2-raphael": { smallText: "Raphael" },
        "gryphe/mythomax-l2-13b": {
          smallText: "Mythomax 13B",
        },
        "open-orca/mistral-7b-openorca": {
          smallText: "Open Orca Mistral 7B",
        },
        "teknium/openhermes-2-mistral-7b": {
          smallText: "OpenHermes Mistral 7B",
        },
        "teknium/openhermes-2.5-mistral-7b": {
          smallText: "OpenHermes 2.5 Mistral 7B",
        },
        "jondurbin/airoboros-l2-70b": {
          smallText: "Airoboros 70B",
        },
        "mistralai/mixtral-8x7b-instruct": {
          smallText: "Mixtral 7B Instruct",
        },
        "mistralai/mistral-medium": {
          smallText: "Mistral Medium",
        },
        "deepseek/deepseek-r1": {
          smallText: "DeepSeek R1",
        },
        "deepseek/deepseek-chat": {
          smallText: "DeepSeek V3",
        },
        "deepseek/deepseek-chat-v3.1": {
          smallText: "DeepSeek V3.1",
        },
        "deepseek/deepseek-v4-pro": {
          smallText: "DeepSeek V4 Pro",
        },
        "mistralai/mistral-large-2411": {
          smallText: "Mistral Large",
        },
        "mistralai/mistral-large-2512": {
          smallText: "Mistral Large 3",
        },
        "moonshotai/kimi-k2": {
          smallText: "Kimi K2",
        },
        "moonshotai/kimi-k2-thinking": {
          smallText: "Kimi K2 Thinking",
        },
        "moonshotai/kimi-k2.6": {
          smallText: "Kimi K2.6",
        },
        "moonshotai/kimi-k3": {
          smallText: "Kimi K3",
        },
        "claude-haiku-5-5": {
          smallText: "Claude 5.5 Haiku",
        },
        "claude-haiku-4-5-20251001": {
          smallText: "Claude 4.5 Haiku",
        },
        "openai/gpt-3.5-turbo": {
          smallText: "GPT 3.5 Turbo",
        },
        "openai/gpt-4": {
          smallText: "GPT 4",
        },
        "aetherwiing/mn-starcannon-12b": {
          smallText: "StarCannon 12B",
        },
      };

      return { largeText: "Generate Prose", smallText: getEntrySmallText(entry, engineTexts, errorCallback), amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/openrouter-interstride",
    transform: (entry, errorCallback) => {
      const engineTexts = {
        "alpindale/goliath-120b": {
          smallText: "Goliath 120B",
        },
        "openai/gpt-3.5-turbo": {
          smallText: "GPT 3.5 Turbo",
        },
        "openai/gpt-4": {
          smallText: "GPT 4",
        },
      };

      return { largeText: "Generate Prose", smallText: getEntrySmallText(entry, engineTexts, errorCallback), amount: entry.amount };
    },
  },
  {
    matchType: "sourcePattern",
    matchValue: "^runDynamicPluginAction/documentPlugins/write/auto/.*$",
    transform: (entry) => {
      if (entry.context?.agenticWriteProseTool === true) {
        return { largeText: "Write", amount: entry.amount };
      }
      const func = entry.source.split("/").pop();

      return { largeText: "Write (Auto)", smallText: writeModelText(entry, func), amount: entry.amount };
    },
  },
  {
    matchType: "sourcePattern",
    matchValue: "^runDynamicPluginAction/documentPlugins/writeClaude3/auto/.*$",
    transform: (entry) => {
      if (entry.context?.agenticWriteProseTool === true) {
        return { largeText: "Write", amount: entry.amount };
      }
      const func = entry.source.split("/").pop();

      return { largeText: "Write (Auto)", smallText: writeModelText(entry, func), amount: entry.amount };
    },
  },
  {
    matchType: "sourcePattern",
    matchValue: "^runDynamicPluginAction/documentPlugins/write/guided/.*$",
    transform: (entry) => {
      if (entry.context?.agenticWriteProseTool === true) {
        return { largeText: "Write", amount: entry.amount };
      }
      const func = entry.source.split("/").pop();

      return { largeText: "Write (Guided)", smallText: writeModelText(entry, func), amount: entry.amount };
    },
  },
  {
    matchType: "sourcePattern",
    matchValue: "^runDynamicPluginAction/documentPlugins/writeClaude3/guided/.*$",
    transform: (entry) => {
      if (entry.context?.agenticWriteProseTool === true) {
        return { largeText: "Write", amount: entry.amount };
      }
      const func = entry.source.split("/").pop();

      return { largeText: "Write (Guided)", smallText: writeModelText(entry, func), amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/write/gpt-4-turbo",
    transform: (entry) => {
      return { largeText: "Generate Prose", smallText: "Balanced", amount: entry.amount };
    },
  },
  {
    matchType: "sourcePattern",
    matchValue: "^runPluginAction/.*$",
    transform: (entry) => {
      const pluginName = entry.context?.pluginInfo?.name || "";
      return { largeText: "Plugin", smallText: pluginName, amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "visualizeCanvas",
    transform: (entry) => {
      return { largeText: "Canvas Visualize", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "canvasOutline",
    transform: (entry) => {
      return { largeText: "Canvas Outline", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "canvasCard",
    transform: (entry) => {
      return { largeText: "Canvas Card", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "canvasOutlineCard",
    transform: (entry) => {
      return { largeText: "Canvas Outline Card", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "analyze",
    transform: (entry) => {
      return { largeText: "Feedback", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "tweetstorm",
    transform: (entry) => {
      return { largeText: "Tweetstorm", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "conversionFromWordPack",
    transform: (entry) => {
      return { largeText: "Conversion from Word Pack", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "conversionFromWords",
    transform: (entry) => {
      return { largeText: "Conversion from Words", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "quickEditChat/chat",
    transform: (entry) => {
      let smallText = "";
      if (entry.amount) {
        smallText = "High Quality";
      }
      return { largeText: "Quick Chat", smallText, amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "runDynamicPluginAction/commandMenuPlugins/instruct/highQuality",
    transform: (entry) => {
      return { largeText: "Quick Edit", smallText: "High Quality", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "runDynamicPluginAction/commandMenuPlugins/instruct/muse",
    transform: (entry) => {
      return { largeText: "Quick Edit", smallText: "High Quality (Muse)", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "runDynamicPluginAction/commandMenuPlugins/instruct/normal",
    transform: (entry) => {
      return { largeText: "Quick Edit", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "testPluginAction",
    transform: (entry) => {
      return { largeText: "Test Plugin", smallText: entry.context?.pluginInfo?.name, amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "testWorkflow",
    transform: (entry) => {
      return { largeText: "Test Workflow", smallText: "Admin only", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/importCharacters",
    transform: (entry) => {
      return { largeText: "Import Characters", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/importWorldbuilding",
    transform: (entry) => {
      return { largeText: "Import Worldbuilding", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/detectCharacters",
    transform: (entry) => {
      return { largeText: "Detect Characters", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "storyEngine/detectWorldbuilding",
    transform: (entry) => {
      return { largeText: "Detect Worldbuilding", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "import/manuscriptReport",
    transform: (entry) => ({ largeText: "Manuscript Report", amount: entry.amount }),
  },
  {
    matchType: "source",
    matchValue: "storyEngine/importNovel",
    transform: (entry) => {
      return { largeText: "Import Novel", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "editor",
    transform: (entry) => {
      const categories = entry.context?.feedbackCategories;
      const categoryNameMap = {
        developmental: "Developmental",
        line: "Line",
        dialogue: "Dialogue",
        copy: "Copy",
        beta_read: "Beta Read",
      };
      let smallText;
      if (categories && categories.length > 0) {
        smallText = categories.map((key) => categoryNameMap[key] || key.charAt(0).toUpperCase() + key.slice(1)).join(", ");
      }
      return { largeText: "Feedback", smallText, amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "generateOnboardingDraft",
    transform: (entry) => {
      return { largeText: "Generate Onboarding Draft", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "partner-bonus",
    transform: (entry) => {
      return { largeText: "Bonus Credits", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "persistentChat",
    transform: (entry) => {
      if (entry.amount === 0) {
        return { largeText: "Chat", amount: entry.amount };
      }

      return { largeText: "Chat", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "proto-projects/convert",
    transform: (entry) => {
      return { largeText: "First Chapter Generation", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "smart-dictation/enhance",
    transform: (entry) => {
      return { largeText: "Smart Dictation Enchancement", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "agenticChat/manuscriptReport",
    transform: (entry) => ({ largeText: "Manuscript Report", amount: entry.amount }),
  },
  {
    matchType: "source",
    matchValue: "agenticChat/generateCharacter",
    transform: (entry) => {
      return { largeText: "Generate Character", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "agenticChat/generateCast",
    transform: (entry) => {
      return { largeText: "Generate Cast", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "agenticChat/generateWorldbuilding",
    transform: (entry) => {
      return { largeText: "Generate Worldbuilding", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "agenticChat/generateOutline",
    transform: (entry) => {
      return { largeText: "Generate Outline", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "agenticChat/manageProjectDocuments/reviewInstruction/gpt-4o-mini",
    transform: (entry) => {
      return { largeText: "Review Document", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "agenticChat/manuscriptCommentCreate",
    transform: (entry) => {
      return { largeText: "Manuscript Comment", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "agenticChat/undo",
    transform: (entry) => {
      return { largeText: "Undo", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "agenticChat/planExecution",
    transform: (entry) => {
      const modeLabel = getAgenticChatModeLedgerLargeText(entry.context?.chatMode);
      if (modeLabel) return { largeText: `Plan execution (${modeLabel})`, amount: entry.amount };
      return { largeText: "Plan execution", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "agenticChat/storyBible",
    transform: (entry) => {
      if (typeof entry.context?.storyBibleLedgerLabel === "string" && entry.context.storyBibleLedgerLabel.trim().length > 0) {
        return { largeText: entry.context.storyBibleLedgerLabel.trim(), amount: entry.amount };
      }
      return { largeText: "Story Bible Edit", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "agenticChat/message",
    transform: (entry) => {
      const modeLabel = getAgenticChatModeLedgerLargeText(entry.context?.chatMode);
      if (modeLabel) return { largeText: modeLabel, amount: entry.amount };
      return { largeText: "Chat Message", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "agenticChat/plan/approve",
    transform: (entry) => {
      const storyBibleLabel = getStoryBibleLedgerContextLabel(entry);
      if (storyBibleLabel) return { largeText: storyBibleLabel, amount: entry.amount };
      const modeLabel = getAgenticChatModeLedgerLargeText(entry.context?.chatMode);
      if (modeLabel) return { largeText: modeLabel, amount: entry.amount };
      return { largeText: "Chat Message", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "agenticChat/plan/duplicate",
    transform: (entry) => {
      const storyBibleLabel = getStoryBibleLedgerContextLabel(entry);
      if (storyBibleLabel) return { largeText: storyBibleLabel, amount: entry.amount };
      const modeLabel = getAgenticChatModeLedgerLargeText(entry.context?.chatMode);
      if (modeLabel) return { largeText: modeLabel, amount: entry.amount };
      return { largeText: "Chat Message", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "agenticChat/plan/resume",
    transform: (entry) => {
      const storyBibleLabel = getStoryBibleLedgerContextLabel(entry);
      if (storyBibleLabel) return { largeText: storyBibleLabel, amount: entry.amount };
      const modeLabel = getAgenticChatModeLedgerLargeText(entry.context?.chatMode);
      if (modeLabel) return { largeText: modeLabel, amount: entry.amount };
      return { largeText: "Chat Message", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "agentic-chat",
    transform: (entry) => {
      const storyBibleLabel = getStoryBibleLedgerContextLabel(entry);
      if (storyBibleLabel) return { largeText: storyBibleLabel, amount: entry.amount };
      const modeLabel = getAgenticChatModeLedgerLargeText(entry.context?.chatMode);
      if (modeLabel) return { largeText: modeLabel, amount: entry.amount };
      return { largeText: "Chat Message", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "agenticChat/commentThread",
    transform: (entry) => {
      return { largeText: "Agentic Comment", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "comment-thread-agent",
    transform: (entry) => {
      return { largeText: "Agentic Comment", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "benchmark-contender",
    transform: (entry) => {
      // The comparison opponent is recorded for accounting but is free. Keep
      // any nonzero entry visible so an unexpected charge is never hidden.
      if (entry.type === "consumption" && entry.amount === 0) return false;
      return { largeText: "Muse 2 Alpha Comparison", amount: entry.amount };
    },
  },
  {
    matchType: "source",
    matchValue: "comparison-contender",
    transform: (entry) => {
      return { largeText: "Generation Comparison", amount: entry.amount };
    },
  },
  // Mobile backend invocations
  {
    matchType: "source",
    matchValue: "mobile/smart-dictation",
    transform: (entry) => {
      return { largeText: "Smart Dictation (Mobile)", amount: entry.amount };
    },
  },
  {
    matchType: "sourcePattern",
    matchValue: "^mobile/.*$",
    transform: (entry) => {
      // Generic fallback for any mobile source
      const sourceName = entry.source.replace("mobile/", "").replace(/-/g, " ");
      const displayName = sourceName.charAt(0).toUpperCase() + sourceName.slice(1);
      return { largeText: `${displayName} (Mobile)`, amount: entry.amount };
    },
  },
];

// Merge all ramble/analyse entries up to a ramble/create entry into a single
// ramble/merged entry.  Assume entries are in reverse chronological order.
export function mergeRambleEntries(entries) {
  const mergedEntries = [];
  let lastAnalyseEntry = null;
  let analyseAmount = 0;

  for (const entry of entries.slice().reverse()) {
    if (entry.source === "ramble/analyse") {
      lastAnalyseEntry = entry;
      analyseAmount += entry.amount;
    } else if (entry.source === "ramble/create") {
      entry.amount += analyseAmount;
      entry.source = "ramble/merged";
      analyseAmount = 0;
      mergedEntries.push(entry);
    } else {
      mergedEntries.push(entry);
    }
  }

  // Handle the case where last entries are analyse entries without a create
  // entry.
  if (analyseAmount > 0 && lastAnalyseEntry !== null) {
    mergedEntries.push({
      ...lastAnalyseEntry,
      amount: analyseAmount,
      source: "ramble/merged",
    });
  }

  return mergedEntries.reverse();
}

// Batch context key per billing source for entity image galleries (see src/services/entityImageGeneration.ts).
const ENTITY_IMAGE_BATCH_CONTEXT_KEYS = {
  characterPortrait: "characterPortraitBatchId",
  worldbuildingImage: "worldbuildingImageBatchId",
};

// Each Generate/Regenerate click in a character portrait or worldbuilding image dialog fires 4
// independent tile requests (each its own invocation and charge — see entityImageGeneration.ts for
// why they aren't a single batched request), tagged with a shared batch id in the entry context. The
// user only took one action, so fold every entry sharing a batch into one displayed line with the
// summed cost, keeping the position (and other fields) of whichever entry is encountered first —
// since entries arrive newest-first, that's the batch's most recently completed tile.
export function mergeEntityImageBatchEntries(entries) {
  const mergedEntries = [];
  const mergedEntryByBatchKey = new Map();

  for (const entry of entries) {
    const contextKey = ENTITY_IMAGE_BATCH_CONTEXT_KEYS[entry.source];
    const batchId = contextKey ? entry.context?.[contextKey] : null;
    if (!batchId) {
      mergedEntries.push(entry);
      continue;
    }

    const batchKey = `${entry.source}:${batchId}`;
    const existingMergedEntry = mergedEntryByBatchKey.get(batchKey);
    if (existingMergedEntry) {
      existingMergedEntry.amount += entry.amount;
    } else {
      const mergedEntry = { ...entry };
      mergedEntryByBatchKey.set(batchKey, mergedEntry);
      mergedEntries.push(mergedEntry);
    }
  }

  return mergedEntries;
}

// whether to match on source or type value in ledger entry
const matcherMap = {
  ledgerEntryType: (matchEntry, ledgerEntry) => ledgerEntry.type === matchEntry.matchValue,
  source: (matchEntry, ledgerEntry) => ledgerEntry.source === matchEntry.matchValue,
  sourcePattern: (matchEntry, ledgerEntry) => new RegExp(matchEntry.matchValue).test(ledgerEntry.source),
};

const transformAmount = (amount) => {
  // in the display we do not want to show the negative sign
  const displayAmount = numberWithCommas(Math.abs(amount));

  if (amount > 0) {
    // when adding credits, use a plus sign
    return `+${displayAmount}`;
  } else {
    // for negatives and 0, do not use a sign
    return displayAmount;
  }
};

// return the props for the component
export const getDisplayInfo = (ledgerEntry, errorCallback) => {
  const match = matches.find((matchEntry) => {
    const matcher = matcherMap[matchEntry.matchType];
    if (typeof matcher === "function") {
      return matcher(matchEntry, ledgerEntry);
    } else {
      return false;
    }
  });

  if (!match) {
    const err = new Error(`No display match found for ledger entry type ${ledgerEntry.type} and source ${ledgerEntry.source}`);
    if (errorCallback) {
      errorCallback(err);
    }
    return { ...ledgerEntry, largeText: "Unknown", amount: transformAmount(ledgerEntry.amount), invocationId: ledgerEntry.context?.invocationId };
  }

  const info = match.transform(ledgerEntry, errorCallback);
  if (!info) {
    // returning false from the transform means we should not display it
    return false;
  }
  info.amount = transformAmount(info.amount);
  info.ledgerEntryId = ledgerEntry.id;
  info.invocationId = ledgerEntry.context?.invocationId;
  return info;
};
